import { Activity, BadgeDollarSign, Car, CarTaxiFront, CreditCard, FileCheck2, IdCard, KeyRound, type LucideIcon, MapPin, MessageCircle, Plug, Radio as RadioIcon, Receipt, ScrollText, Settings as SettingsIcon, ShieldCheck, Siren, Star, TrendingUp, Users as UsersIcon, Wrench } from 'lucide-react';
import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Empty, NoPermission, SessionGate, Spinner } from '@despachador/ui';
import { roleName } from './lib/people';
import { Shell } from './lib/Shell';
import { api, SessionProvider, useSession, type Me } from './lib/session';
import { RadioBar, RadioProvider } from './lib/radio';
import { PanicProvider, usePanicAlerts } from './lib/panic';
import { PanicStrip } from './pages/panic/PanicStrip';

// route level code splitting: each section loads on demand
const page = <K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));
const LiveOps = page(() => import('./pages/LiveOps'), 'LiveOps');
const PanicAlerts = page(() => import('./pages/PanicAlerts'), 'PanicAlerts');
const Indicators = page(() => import('./pages/Dashboard'), 'Indicators');
const Services = page(() => import('./pages/Services'), 'Services');
const Radio = page(() => import('./pages/Radio'), 'Radio');
const Drivers = page(() => import('./pages/Drivers'), 'Drivers');
const Fleet = page(() => import('./pages/Fleet'), 'Fleet');
const Documents = page(() => import('./pages/Documents'), 'Documents');
const Customers = page(() => import('./pages/Customers'), 'Customers');
const Quotas = page(() => import('./pages/Quotas'), 'Quotas');
const Loyalty = page(() => import('./pages/loyalty/Loyalty'), 'Loyalty');
const Pricing = page(() => import('./pages/Pricing'), 'Pricing');
const Reports = page(() => import('./pages/Reports'), 'Reports');
const Locations = page(() => import('./pages/Locations'), 'Locations');
const Users = page(() => import('./pages/Users'), 'Users');
const Settings = page(() => import('./pages/Settings'), 'Settings');
const Subscription = page(() => import('./pages/Subscription'), 'Subscription');
const Integrations = page(() => import('./pages/Integrations'), 'Integrations');
const Audit = page(() => import('./pages/Audit'), 'Audit');
const Conversations = page(() => import('./pages/Conversations'), 'Conversations');
const System = page(() => import('./pages/System'), 'System');
const Account = lazy(() => import('./pages/Account').then((m) => ({ default: m.Account })));
/**
 * The platform operators' console: its own sign-in and session, no tenant. In production it only
 * works on PLATFORM_HOST (the API answers `/platform/*` 404 on any other host).
 */
const Console = lazy(() => import('./console/Console').then((m) => ({ default: m.Console })));
const CONSOLE_PATH = '/consola';

/**
 * Navigation grouped by intention. A label is always the title of the screen it opens, and the
 * permission/feature of each entry decides who sees it (the API enforces the same permissions).
 */
const GROUPS = ['Operación', 'Conductores y flota', 'Clientes', 'Finanzas', 'Análisis', 'Configuración', 'Administración'] as const;

interface NavItem {
  group: (typeof GROUPS)[number];
  to: string;
  label: string;
  icon: LucideIcon;
  /** Required permission; a list means any of them. */
  perm?: string | string[];
  feature?: string;
  element: ReactNode;
  /** The page's tabs as its own `useTab` lists them (screen 18: a submenu shortcut); each with who may see it. */
  tabs?: NavTab[];
  /** The query parameter of those tabs, when it is not `tab`. */
  tabParam?: string;
}

interface NavTab {
  key: string;
  label: string;
  perm?: string | string[];
  feature?: string;
}

const NAV: NavItem[] = [
  { group: 'Operación', to: '/en-vivo', label: 'En vivo', icon: Activity, perm: 'dashboard.view', element: <LiveOps /> },
  { group: 'Operación', to: '/servicios', label: 'Servicios', icon: CarTaxiFront, perm: 'services.view', element: <Services /> },
  { group: 'Operación', to: '/alertas-panico', label: 'Alertas de pánico', icon: Siren, perm: 'panic.view', element: <PanicAlerts /> },
  { group: 'Operación', to: '/conversaciones', label: 'Conversaciones', icon: MessageCircle, perm: 'customers.view', feature: 'whatsapp', element: <Conversations /> },
  {
    group: 'Operación',
    to: '/radio',
    label: 'Radio',
    icon: RadioIcon,
    perm: 'communication.view',
    feature: 'ptt',
    element: <Radio />,
    tabs: [
      { key: 'console', label: 'Consola' },
      { key: 'channels', label: 'Canales', perm: ['communication.manage_channels', 'communication.manage_members'] },
      { key: 'history', label: 'Historial', perm: 'communication.view_history' },
      { key: 'restrictions', label: 'Restricciones', perm: 'communication.moderate' },
      { key: 'settings', label: 'Ajustes', perm: 'settings.manage' },
    ],
  },
  { group: 'Conductores y flota', to: '/conductores', label: 'Conductores', icon: IdCard, perm: 'drivers.view', element: <Drivers /> },
  { group: 'Conductores y flota', to: '/flota', label: 'Vehículos', icon: Car, perm: 'vehicles.view', element: <Fleet /> },
  {
    group: 'Conductores y flota',
    to: '/documentos',
    label: 'Documentos',
    icon: FileCheck2,
    perm: 'documents.view',
    element: <Documents />,
    tabs: [
      { key: 'por-revisar', label: 'Por revisar' },
      { key: 'por-vencer', label: 'Por vencer' },
      { key: 'tipos', label: 'Tipos de documento' },
    ],
  },
  { group: 'Clientes', to: '/clientes', label: 'Clientes', icon: UsersIcon, perm: 'customers.view', element: <Customers /> },
  { group: 'Clientes', to: '/fidelizacion', label: 'Fidelización', icon: Star, perm: 'loyalty.view', feature: 'loyalty', element: <Loyalty /> },
  // money, apart from the fleet (RBAC-002): what the drivers pay the company and what the company pays the platform
  {
    group: 'Finanzas',
    to: '/cuotas',
    label: 'Cuotas',
    icon: CreditCard,
    perm: 'quotas.view',
    feature: 'driver_quotas',
    element: <Quotas />,
    tabs: [
      { key: 'overview', label: 'Estado' },
      { key: 'plans', label: 'Planes' },
      { key: 'policy', label: 'Política' },
      { key: 'methods', label: 'Métodos de pago' },
    ],
  },
  { group: 'Finanzas', to: '/suscripcion', label: 'Suscripción', icon: Receipt, perm: 'billing.view', element: <Subscription /> },
  { group: 'Análisis', to: '/indicadores', label: 'Indicadores', icon: TrendingUp, perm: ['dashboard.view', 'reports.view'], element: <Indicators /> },
  { group: 'Análisis', to: '/reportes', label: 'Reportes', icon: ScrollText, perm: 'reports.view', element: <Reports /> },
  {
    group: 'Configuración',
    to: '/configuracion',
    label: 'Empresa y despacho',
    icon: SettingsIcon,
    perm: ['settings.manage', 'branding.manage', 'dispatch.configure'],
    element: <Settings />,
    tabs: [
      { key: 'general', label: 'General', perm: 'settings.manage' },
      { key: 'brand', label: 'Marca', perm: 'branding.manage' },
      { key: 'dispatch', label: 'Despacho', perm: 'dispatch.configure' },
      { key: 'features', label: 'Funcionalidades', perm: 'settings.manage' },
    ],
  },
  {
    group: 'Configuración',
    to: '/integraciones',
    label: 'Integraciones',
    icon: Plug,
    perm: ['integrations.manage', 'settings.manage'],
    element: <Integrations />,
    tabs: [
      { key: 'whatsapp', label: 'Número de WhatsApp', perm: 'integrations.manage' },
      { key: 'texts', label: 'Textos de WhatsApp', perm: 'settings.manage' },
    ],
  },
  {
    group: 'Configuración',
    to: '/tarifas',
    label: 'Tarifas y zonas',
    icon: BadgeDollarSign,
    perm: 'pricing.view',
    element: <Pricing />,
    tabs: [
      { key: 'tariffs', label: 'Tarifas' },
      { key: 'zones', label: 'Zonas' },
      { key: 'rules', label: 'Reglas' },
      { key: 'holidays', label: 'Festivos' },
    ],
  },
  // mostly the company's own place knowledge (places, imports, aliases, suggestions), not analytics
  {
    group: 'Configuración',
    to: '/ubicaciones',
    label: 'Ubicaciones',
    icon: MapPin,
    perm: ['locations.view', 'locations.manage', 'settings.manage'],
    element: <Locations />,
    tabs: [
      { key: 'summary', label: 'Resumen', perm: ['locations.view', 'locations.manage'] },
      { key: 'failures', label: 'Fallos', perm: ['locations.view', 'locations.manage'] },
      { key: 'suggestions', label: 'Sugerencias', perm: ['locations.view', 'locations.manage'] },
      { key: 'places', label: 'Lugares', perm: ['locations.view', 'locations.manage'] },
      { key: 'aliases', label: 'Alias', perm: ['locations.view', 'locations.manage'] },
      { key: 'imports', label: 'Importar', perm: ['locations.import', 'locations.manage'] },
      { key: 'searches', label: 'Búsquedas', perm: ['locations.view', 'locations.manage'] },
      { key: 'municipalities', label: 'Municipios', perm: 'settings.manage' },
    ],
  },
  {
    group: 'Administración',
    to: '/usuarios',
    label: 'Usuarios y roles',
    icon: KeyRound,
    perm: 'users.view',
    element: <Users />,
    tabs: [
      { key: 'users', label: 'Usuarios' },
      { key: 'roles', label: 'Roles y permisos' },
    ],
  },
  {
    group: 'Administración',
    to: '/auditoria',
    label: 'Auditoría',
    icon: ShieldCheck,
    perm: ['audit.view', 'security.view_events'],
    element: <Audit />,
    // the page picks its view with ?vista=; each view needs its own permission
    tabParam: 'vista',
    tabs: [
      { key: 'cambios', label: 'Registro de cambios', perm: 'audit.view' },
      { key: 'seguridad', label: 'Seguridad', perm: 'security.view_events' },
    ],
  },
  { group: 'Administración', to: '/sistema', label: 'Sistema', icon: Wrench, perm: 'settings.manage', element: <System /> },
];

/** Where each role starts: the screen that answers "what do I need to do now?" for that job. */
const ROLE_HOME: Record<string, string> = {
  DISPATCHER: '/en-vivo',
  SUPERVISOR: '/en-vivo',
  CALL_CENTER: '/servicios',
  FLEET_MANAGER: '/documentos',
  ACCOUNTING: '/cuotas',
  TENANT_OWNER: '/indicadores',
  ADMIN: '/indicadores',
};

/**
 * Addresses of sections moved in RBAC-002 («Catálogos y ajustes» split into its modules; the plan
 * and WhatsApp out of «Empresa y despacho»): saved links and bookmarks open the new place.
 */
const MOVED: Record<string, Record<string, string>> = {
  '/catalogos': { '': '/documentos?tab=tipos', doctypes: '/documentos?tab=tipos', methods: '/cuotas?tab=methods', municipalities: '/ubicaciones?tab=municipalities', radio: '/radio?tab=settings' },
  '/configuracion': { plan: '/suscripcion', whatsapp: '/integraciones', texts: '/integraciones?tab=texts' },
};
const movedTo = (pathname: string, search: string): string | null => MOVED[pathname]?.[new URLSearchParams(search).get('tab') ?? ''] ?? null;

const allowed = (perm: NavItem['perm'], has: (p: string) => boolean) => !perm || (Array.isArray(perm) ? perm.some(has) : has(perm));

/** The tabs of a section this account sees, as the page filters them; one alone makes no submenu. */
function visibleTabs(n: NavItem, can: (p: string) => boolean, feature: (f: string) => boolean) {
  const tabs = (n.tabs ?? []).filter((t) => allowed(t.perm, can) && (!t.feature || feature(t.feature)));
  return tabs.length > 1 ? tabs.map(({ key, label }) => ({ key, label })) : undefined;
}

/** The panel has something for this account: at least one section of the menu. */
const hasPanelSection = (me: Me) => {
  const perms = new Set(me.permissions);
  return NAV.some((n) => allowed(n.perm, (p) => perms.has(p)) && (!n.feature || me.features[n.feature]));
};

export function App() {
  return (
    <BrowserRouter>
      {window.location.pathname.startsWith(CONSOLE_PATH) ? (
        <Suspense fallback={<Spinner block />}>
          <Console />
        </Suspense>
      ) : (
        <Gate />
      )}
    </BrowserRouter>
  );
}

/** One start-up for the whole panel: the session, the screens without one and the account (SessionGate). */
function Gate() {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SessionGate<Me>
      api={api}
      app="admin"
      appName="Operación"
      subtitle="Panel de operación"
      platform="WEB"
      layout="split"
      router={{ pathname: location.pathname, search: location.search, navigate: (to, opts) => navigate(to, opts) }}
      acceptAccount={(me) => (hasPanelSection(me) ? null : 'app-no-permitida')}
    >
      {({ me, reload, signOut }) => (
        <SessionProvider me={me} reload={reload}>
          <RadioProvider>
            <PanicProvider>
              <Layout onLogout={() => void signOut()} />
            </PanicProvider>
          </RadioProvider>
        </SessionProvider>
      )}
    </SessionGate>
  );
}

function Layout({ onLogout }: { onLogout: () => void }) {
  const { me, can, feature } = useSession();
  const panic = usePanicAlerts();
  const location = useLocation();
  const items = NAV.filter((n) => allowed(n.perm, can) && (!n.feature || feature(n.feature)));
  const home = me.user.roles.map((r) => ROLE_HOME[r]).find((to) => to && items.some((n) => n.to === to)) ?? items[0]?.to ?? '/';
  const current = items.find((n) => location.pathname.startsWith(n.to));
  // links saved before the dashboard was split (/?tab=finance&preset=last7) keep working
  const legacyAnalytics = location.pathname === '/' && /[?&](tab|preset)=/.test(location.search) && items.some((n) => n.to === '/indicadores');
  const moved = movedTo(location.pathname, location.search);

  // the browser tab and history entries say where you are
  useEffect(() => {
    document.title = `${current?.label ?? 'Operación'} · ${me.branding.brandName}`;
  }, [current, me.branding.brandName]);

  const groups = GROUPS.map((g) => ({
    title: g,
    items: items
      .filter((n) => n.group === g)
      .map((n) => ({
        to: n.to,
        label: n.label,
        icon: n.icon,
        tabParam: n.tabParam,
        // the tabs this account sees; a single one is no submenu (the page shows no tabs either)
        tabs: visibleTabs(n, can, feature),
        // open silent alerts: the live board counts them in red, on every screen
        badge:
          n.to === '/en-vivo' && panic.open.length > 0 ? (
            <span className="nav-count danger">
              {panic.open.length}
              <span className="sr-only"> alertas de pánico abiertas</span>
            </span>
          ) : undefined,
      })),
  }));

  return (
    <Shell
      brand={{ name: me.branding.brandName, logoUrl: me.branding.logoUrl }}
      groups={groups}
      user={{ name: me.user.fullName, subtitle: me.user.roles.map(roleName).join(', '), accountTo: '/mi-cuenta', accountLabel: 'Mi cuenta y seguridad' }}
      onLogout={onLogout}
      menuBadge={
        panic.open.length > 0 ? (
          <span className="menu-count">
            {panic.open.length}
            <span className="sr-only"> alertas de pánico abiertas</span>
          </span>
        ) : undefined
      }
      aboveMain={<PanicStrip />}
      belowMain={location.pathname !== '/radio' ? <RadioBar /> : undefined}
    >
      <Suspense fallback={<Spinner block />}>
        {moved ? <Navigate to={moved} replace /> : <Routes>
          {items.map((n) => (
            <Route key={n.to} path={n.to} element={n.element} />
          ))}
          {/* a section this account cannot use says so (the API refuses it anyway) */}
          {NAV.filter((n) => !allowed(n.perm, can)).map((n) => (
            <Route
              key={n.to}
              path={n.to}
              element={
                <NoPermission
                  action={
                    <Link className="btn" to={home}>
                      Ir al inicio
                    </Link>
                  }
                />
              }
            />
          ))}
          {/* allowed, but the company has the feature off: say so instead of jumping home (docs/audits/2026-10-05-navigation.md H-05) */}
          {NAV.filter((n) => allowed(n.perm, can) && n.feature && !feature(n.feature)).map((n) => (
            <Route
              key={n.to}
              path={n.to}
              element={
                <Empty
                  title={`${n.label} no está activa en tu empresa`}
                  action={
                    <Link className="btn" to={home}>
                      Ir al inicio
                    </Link>
                  }
                >
                  Un administrador puede activarla en Empresa y despacho → Funcionalidades.
                </Empty>
              }
            />
          ))}
          <Route path="/mi-cuenta" element={<Account />} />
          <Route path="*" element={<Navigate to={legacyAnalytics ? `/indicadores${location.search}` : home} replace />} />
        </Routes>}
      </Suspense>
    </Shell>
  );
}
